import { LoadingOverlay } from '@mantine/core'
import { isNil } from '@semios/app-platform-common'
import type { VV } from '@semios/app-platform-value-type-definitions'
import { WindMachineViewDetails } from 'App/Map/PanelDetails/_utils/by-domain/windMachine/WindMachineViewDetailsMenu/WideScreen/WindMachineViewDetails'
import type { TLngLat, TWindMachineDetailsElement } from 'App/Map/types'
import { ModalDrawer } from 'components/ModalDrawer/ModalDrawer'
import { translate } from 'i18n/i18n'
import type { FC } from 'react'
import { useEffect, useState } from 'react'
import { SharedSettings } from 'settings/SharedSettings'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import { selectedFieldAssetsStore } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import { smallStore } from 'stores/smallStore/smallStore'
import { apiFetch } from 'utils/apiFetch'
import { GroupControlFilters } from './MenuComponents/GroupControlFilters/GroupControlFilters'
import type { TWMDetails } from './types'

const possibleValueTypesToRequest: VV.DomainTypes.WindMachine.TTimeseriesValueTypeKeysMerged[] = [
  'windMachineActivity',
  'windMachineActivityIntervals',
  'windMachineBattery',
  'windMachineCycles',
  'windMachineFuel',
  'windMachineRPM',
  'windMachineRunTime',
  'windMachineSettings',
  'windMachineTemperature',
]

export const WindMachineViewDetailsMenu: FC = () => {
  const opened = smallStore.useSelector((s) => s.showWindMachineViewDetails)
  const [dataDate, setDataDate] = useState<Record<string, string | null>>({})
  const [windMachineStoppedAt, setWindMachineStoppedAt] = useState<Record<string, number | null>>({})
  const [isAuto, setIsAuto] = useState<Record<string, boolean>>({})
  const [wmData, setWmData] = useState<TWMDetails>({} as TWMDetails)
  const [loading, setLoading] = useState(false)

  const wmState = {
    dataDate,
    setDataDate,
    windMachineStoppedAt,
    setWindMachineStoppedAt,
    isAuto,
    setIsAuto,
  }

  const { property } = selectedFieldAssetsStore.useSelector((s) => s)
  const { properties } = fieldAssetStore.getState()

  const selectedLngLats =
    isNil(properties) || isNil(property)
      ? []
      : Object.values(properties[property]?.points || []).reduce((acc: string[], point) => {
          const typesOnPointOfWmType = possibleValueTypesToRequest.filter((valueType) =>
            point.valuesTimeseries.includes(valueType),
          )

          if (typesOnPointOfWmType.length > 0) acc.push(point.lngLat)

          return acc
        }, [])

  const fetchData = async () => {
    setLoading(true)

    try {
      if (isNil(selectedLngLats) || selectedLngLats.length === 0) return

      const res = await apiFetch({
        url: '/wind-machine',
        body: {
          windMachineDetails: {
            lngLats: selectedLngLats,
          },
        },
      })

      if (res.windMachineDetails !== undefined) {
        const windMachineData = res.windMachineDetails

        setWmData(windMachineData)
      } else {
        return
      }
    } catch (error) {
      return
    }

    setLoading(false)
  }

  useEffect(() => {
    if (opened) fetchData()
  }, [opened])

  useEffect(() => {
    const autoInitialState: Record<string, boolean> = {}
    const dateInitialState: Record<string, string | null> = {}

    if (!isNil(wmData) && !isNil(property)) {
      wmData[property]?.windMachineDetails.forEach((wmDetails) => {
        const key: TLngLat = `POINT(${wmDetails.geom.coordinates[0]} ${wmDetails.geom.coordinates[1]})`

        dateInitialState[key] = wmDetails.equipmentStatus.date

        if (
          wmDetails?.applicationSettingsNew?.status === 'new' &&
          wmDetails?.applicationSettingsNew?.settings?.auto !== undefined
        ) {
          autoInitialState[key] = !!wmDetails?.applicationSettingsNew?.settings?.auto
        } else {
          autoInitialState[key] = !!wmDetails.equipmentStatus.autoMode
        }
      })

      setIsAuto(autoInitialState)

      setDataDate(dateInitialState)
    } else {
      setIsAuto({})

      setDataDate({})
    }
  }, [wmData, property])

  const wmDataForProperty = (!isNil(property) && wmData?.[property]) || ({} as TWindMachineDetailsElement)

  return (
    <ModalDrawer
      opened={opened}
      onClose={() => smallStore.setState((s) => ({ ...s, showWindMachineViewDetails: false }))}
      title={translate.phrases.banyanApp('Wind Machine Controls')}
      size={'500px'}
      zIndex={SharedSettings.DEFAULT_MODAL_DRAWER_Z_INDEX}
    >
      <LoadingOverlay visible={loading} zIndex={13} />
      <GroupControlFilters content={wmDataForProperty} fetchData={fetchData} />
      <WindMachineViewDetails data={wmDataForProperty} state={wmState} fetchData={fetchData} />
    </ModalDrawer>
  )
}
